import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  ApplicationRef,
  ErrorHandler,
  effect,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection
} from '@angular/core';
import {
  bootstrapApplication,
  enableDebugTools
} from '@angular/platform-browser';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
  withViewTransitions
} from '@angular/router';

import {
  AuthHttpInterceptor,
  authHttpInterceptorFn,
  provideAuth0
} from '@auth0/auth0-angular';
import { datadogRum } from '@datadog/browser-rum';
import { provideTransloco } from '@ngneat/transloco';
import {
  browserTracingIntegration,
  createErrorHandler,
  init
} from '@sentry/angular';
import posthog from 'posthog-js';

import { AppComponent } from 'src/app/axle-app.component';
import {
  modalPreviewAuthProviders,
  modalPreviewStubInterceptorFn
} from 'src/app/dev/modal-preview/stubs/modal-preview.providers';
import { removeTokenFromRumEvent } from 'src/app/shared/functions/rum.functions';
import { legacyPaginatorInterceptor } from 'src/app/shared/interceptors/legacy-paginator.interceptor';
import {
  auth0LogoutInterceptorFn,
  publicTokenInterceptorFn,
  whitelistInterceptorFn
} from 'src/app/shared/interceptors/shared-interceptors';
import { AxleToastService } from 'src/app/shared/services/toast/axle-toast.service';
import { UserStore } from 'src/app/shared/services/user/user.state';
import { isLoginPrompt } from 'src/app/shared/state/authentication/authentication.methods';
import { isIdpInitiatedFlow } from 'src/app/shared/utils/auth0-idp.utils';
import { TranslocoHttpLoader } from 'src/app/transloco-loader';
import { appVersion } from 'src/app/version';
import { environment } from 'src/environments/environment';
import { LOCAL_DATADOG_RUM_KEY } from 'src/environments/environment.constants';
import { routes } from 'src/routes';

// Window augmentations for pylon/Pylon moved to src/globals.d.ts so the test
// program sees them too — see the note in that file.

const isLocalHost =
  window.location.host.includes('127.0.0.1') ||
  window.location.host.includes('localhost');

async function initializePosthog(): Promise<void> {
  if (isLocalHost) return;

  posthog.init(environment.posthog.apiKey, {
    api_host: 'https://app.posthog.com',
    secure_cookie: true,
    autocapture: false,
    disable_session_recording: !environment.posthog.sessionRecording
  });
}

async function initializeSentry(): Promise<void> {
  const toast = inject(AxleToastService);
  const userStore = inject(UserStore);
  await init({
    dsn: 'https://4ad21a6729c420b2d3cac55ebf45161c@o4506972498952192.ingest.us.sentry.io/4506972507996160',
    integrations: [browserTracingIntegration()],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      'localhost',
      'https://127.0.0.1:8443',
      'api-dev.axleaccess.com',
      'axle-qa.herokuapp.com',
      'api-sandbox.axleaccess.com',
      'api-integrations.axleaccess.com',
      'api.axleaccess.com'
    ],
    beforeSend: (event) => {
      event.tags = {
        ...event.tags,
        env: environment.env,
        version: appVersion,
        userId: userStore.userInfo()?.id
      };

      const errorMessage = event.exception?.values?.[0]?.value;
      if (
        errorMessage?.includes(
          'View transition was skipped because document visibility state is hidden.'
        )
      ) {
        return null;
      }

      if (
        errorMessage?.includes('Failed to fetch dynamically imported module')
      ) {
        const reloadCountKey = 'moduleImportReloadCount';
        const currentCount = parseInt(
          sessionStorage.getItem(reloadCountKey) || '0',
          10
        );

        if (currentCount < 1) {
          sessionStorage.setItem(reloadCountKey, (currentCount + 1).toString());
          window.location.reload();
        }
        return null;
      } else if (!event.tags?.['suppressToast']) {
        toast.addError(
          "Something went wrong. Please contact support in the bottom left if it doesn't resolve."
        );
      }

      return event;
    }
  });
}

function getAuth0Config(): typeof environment.auth0 {
  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];

  const urlParams = new URLSearchParams(window.location.search);
  const loginType = urlParams.get('lt');
  const prompt = urlParams.get('prompt');

  // Base configuration
  let config = environment.auth0;

  if (loginType === 'p') {
    config = {
      ...config,
      authorizationParams: {
        ...config.authorizationParams,
        connection: 'Username-Password-Authentication'
      }
    };
  }

  if (isLoginPrompt(prompt)) {
    config = {
      ...config,
      authorizationParams: {
        ...config.authorizationParams,
        prompt
      }
    };
  }

  const subdomainConfigs: Record<string, typeof environment.auth0> = {
    canaccord: {
      ...config,
      authorizationParams: {
        ...config.authorizationParams,
        connection: 'canaccord-genuity-sso'
      }
    }
  };

  config = subdomainConfigs[subdomain] || config;

  // Skip Auth0's redirect callback handling for idP-initiated flows
  // We'll handle the redirect manually in the app component
  if (isIdpInitiatedFlow()) {
    config = {
      ...config,
      skipRedirectCallback: true
    };
  }

  return config;
}

function initializeDatadogRum(): void {
  if (isLocalHost && localStorage.getItem(LOCAL_DATADOG_RUM_KEY) !== 'true')
    return;

  datadogRum.init({
    applicationId: '9d852541-ab0a-468f-aa53-212b96fb8345',
    clientToken: 'pubb82310aad4d388fc43f5a358649cee69',
    site: 'us5.datadoghq.com',
    service: 'axle',
    env: isLocalHost ? 'local' : environment.env,
    version: appVersion,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: removeTokenFromRumEvent,
    allowedTracingUrls: [
      {
        match: /^https:\/\/api(-[a-z0-9]+)?\.axleaccess\.com/,
        propagatorTypes: ['datadog']
      }
    ]
  });

  const userStore = inject(UserStore);
  effect(() => {
    const userId = userStore.userInfo()?.id;
    if (userId) {
      datadogRum.setUser({ id: userId });
    } else {
      datadogRum.clearUser();
    }
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false }
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(
      routes,
      withViewTransitions(),
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })
    ),
    provideZonelessChangeDetection(),
    provideTransloco({
      config: {
        availableLangs: [
          {
            id: 'en',
            label: 'English'
          },
          { id: 'es', label: 'Spanish' },
          { id: 'fr', label: 'French' }
        ],
        defaultLang: 'en',
        reRenderOnLangChange: true
        // prodMode: environment.production
      },
      loader: TranslocoHttpLoader
    }),
    provideAuth0(getAuth0Config()),
    AuthHttpInterceptor,
    provideHttpClient(
      withInterceptors([
        // First so a stubbed request short-circuits before the auth
        // interceptors run. Inert unless the modal preview harness arms it.
        modalPreviewStubInterceptorFn,
        auth0LogoutInterceptorFn,
        authHttpInterceptorFn,
        whitelistInterceptorFn,
        publicTokenInterceptorFn,
        legacyPaginatorInterceptor
      ])
    ),
    ...modalPreviewAuthProviders(),
    provideAppInitializer(() => initializeSentry()),
    provideAppInitializer(() => initializePosthog()),
    provideAppInitializer(() => initializeDatadogRum()),
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false }
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false,
        dialogOptions: {
          enabled: false
        },
        logErrors: true
      })
    }
  ]
}).then((module) =>
  enableDebugTools(module.injector.get(ApplicationRef).components[0])
);
